import React, { useState, useEffect } from 'react';
import {
  Card, Table, Button, Modal, Form, Input, Space, Tooltip,
  Popconfirm, notification, Tag, Drawer, Transfer, Empty, Spin,
  Divider, Typography, List, Avatar, Select, Tabs, Badge,
  Switch
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  TeamOutlined,
  UsergroupAddOutlined,
  SearchOutlined,
  InfoCircleOutlined,
  UserOutlined,
  UserAddOutlined,
  MailOutlined,
  ReloadOutlined,
  SendOutlined
} from '@ant-design/icons';
import userService from '../../services/userService';
import messageService from '../../services/messageService';
import MyTable from '../common/MyTable';
const { TextArea } = Input;
const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

/**
 * AdminUserGroups component for managing user groups
 */
const AdminUserGroups = () => {
  // State for user groups data
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  
  // State for group modal
  const [groupModalVisible, setGroupModalVisible] = useState(false);
  const [groupModalMode, setGroupModalMode] = useState('create'); // 'create' or 'edit'
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupForm] = Form.useForm();
  
  // State for user transfer drawer
  const [userTransferVisible, setUserTransferVisible] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUserKeys, setSelectedUserKeys] = useState([]);
  const [groupMembers, setGroupMembers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [searchUserText, setSearchUserText] = useState('');
  
  // State for group details drawer
  const [groupDetailsVisible, setGroupDetailsVisible] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  
  // State for message modal
  const [messageModalVisible, setMessageModalVisible] = useState(false);
  const [messageForm] = Form.useForm();
  const [sendingMessage, setSendingMessage] = useState(false);
  
  // Active tab state
  const [activeTab, setActiveTab] = useState('groups');
  
  // Fetch user groups on mount
  useEffect(() => {
    fetchUserGroups();
  }, []);
  
  // Fetch user groups
  const fetchUserGroups = async () => {
    try {
      setLoading(true);
      const response = await userService.getUserGroups();
      setGroups(response.groups || []);
      setPagination({
        ...pagination,
        total: response.total || response.groups?.length || 0
      });
    } catch (error) {
      console.error('Error fetching user groups:', error);
      notification.error({
        message: 'Failed to load user groups',
        description: error.message || 'Please try again later'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch all users for transfer component
  const fetchAllUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await userService.getAllCustomers({ limit: 100 });
      
      const formattedUsers = (response.data || []).map(user => ({
        key: user.id.toString(),
        title: user.name || user.email,
        description: user.email,
        avatar: user.avatar
      }));
      
      setAllUsers(formattedUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
      notification.error({
        message: 'Failed to load users',
        description: error.message || 'Please try again later'
      });
    } finally {
      setLoadingUsers(false);
    }
  };
  
  // Fetch group details with members
  const fetchGroupDetails = async (groupId) => {
    try {
      setDetailsLoading(true);
      const response = await userService.getUserGroupDetails(groupId);
      
      // Update selected group with members
      setSelectedGroup({
        ...response.group,
        members: response.group.users || []
      });
      
      // Update group members for transfer component if needed
      if (userTransferVisible) {
        const memberKeys = (response.group.users || []).map(user => user.id.toString());
        setGroupMembers(memberKeys);
        setSelectedUserKeys(memberKeys);
      }
    } catch (error) {
      console.error('Error fetching group details:', error);
      notification.error({
        message: 'Failed to load group details',
        description: error.message || 'Please try again later'
      });
    } finally {
      setDetailsLoading(false);
    }
  };
  
  // Create a new user group
  const createUserGroup = async (values) => {
    try {
      const response = await userService.createUserGroup(values);
      
      notification.success({
        message: 'Group Created',
        description: 'User group has been created successfully'
      });
      
      // Refresh the groups list
      fetchUserGroups();
      
      return response;
    } catch (error) {
      console.error('Error creating user group:', error);
      notification.error({
        message: 'Failed to create user group',
        description: error.message || 'Please try again later'
      });
      throw error;
    }
  };
  
  // Update an existing user group
  const updateUserGroup = async (groupId, values) => {
    try {
      const response = await userService.updateUserGroup(groupId, values);
      
      notification.success({
        message: 'Group Updated',
        description: 'User group has been updated successfully'
      });
      
      // Refresh the groups list
      fetchUserGroups();
      
      return response;
    } catch (error) {
      console.error('Error updating user group:', error);
      notification.error({
        message: 'Failed to update user group',
        description: error.message || 'Please try again later'
      });
      throw error;
    }
  };
  
  // Delete a user group
  const deleteUserGroup = async (groupId) => {
    try {
      await userService.deleteUserGroup(groupId);
      
      notification.success({
        message: 'Group Deleted',
        description: 'User group has been deleted successfully'
      });
      
      // Refresh the groups list
      fetchUserGroups();
    } catch (error) {
      console.error('Error deleting user group:', error);
      notification.error({
        message: 'Failed to delete user group',
        description: error.message || 'Please try again later'
      });
    }
  };
  
  // Add users to a group
  const addUsersToGroup = async (groupId, userIds) => {
    try {
      await userService.addUsersToGroup(groupId, userIds);
      
      notification.success({
        message: 'Users Added',
        description: 'Users have been added to the group successfully'
      });
      
      // Refresh group details
      fetchGroupDetails(groupId);
    } catch (error) {
      console.error('Error adding users to group:', error);
      notification.error({
        message: 'Failed to add users',
        description: error.message || 'Please try again later'
      });
    }
  };
  
  // Remove users from a group
  const removeUsersFromGroup = async (groupId, userIds) => {
    try {
      await userService.removeUsersFromGroup(groupId, userIds);
      
      notification.success({
        message: 'Users Removed',
        description: 'Users have been removed from the group successfully'
      });
      
      // Refresh group details
      fetchGroupDetails(groupId);
    } catch (error) {
      console.error('Error removing users from group:', error);
      notification.error({
        message: 'Failed to remove users',
        description: error.message || 'Please try again later'
      });
    }
  };
  
  // Send message to a group
  const sendMessageToGroup = async (values) => {
    try {
      setSendingMessage(true);
      
      const formData = new FormData();
      formData.append('subject', values.subject);
      formData.append('message', values.message);
      formData.append('message_type', 'group');
      formData.append('group_id', selectedGroup.id);
      formData.append('importance', values.importance || 'normal');
      formData.append('send_notification', values.send_notification ? 1 : 0);
      
      const response = await messageService.sendMessage(formData);
      
      notification.success({
        message: 'Message Sent',
        description: `Message has been sent to ${selectedGroup.name} group members`
      });
      
      // Close modal and reset form
      setMessageModalVisible(false);
      messageForm.resetFields();
      
      return response;
    } catch (error) {
      console.error('Error sending message to group:', error);
      notification.error({
        message: 'Failed to send message',
        description: error.message || 'Please try again later'
      });
    } finally {
      setSendingMessage(false);
    }
  };
  
  // Show group modal for creating/editing
  const showGroupModal = (mode, group = null) => {
    setGroupModalMode(mode);
    setSelectedGroup(group);
    
    if (mode === 'edit' && group) {
      groupForm.setFieldsValue({
        name: group.name,
        description: group.description
      });
    } else {
      groupForm.resetFields();
    }
    
    setGroupModalVisible(true);
  };
  
  // Handle group form submission
  const handleGroupFormSubmit = async () => {
    try {
      const values = await groupForm.validateFields();
      
      if (groupModalMode === 'create') {
        await createUserGroup(values);
      } else {
        await updateUserGroup(selectedGroup.id, values);
      }
      
      setGroupModalVisible(false);
    } catch (error) {
      // Form validation error, handled by form component
      console.error('Form submission error:', error);
    }
  };
  
  // Show user transfer drawer
  const showUserTransfer = (group) => {
    setSelectedGroup(group);
    setUserTransferVisible(true);
    
    // Fetch members for this group
    fetchGroupDetails(group.id);
    
    // Fetch all users for selection
    fetchAllUsers();
  };
  
  // Handle user transfer changes
  const handleUserTransferChange = (nextSelectedKeys) => {
    setSelectedUserKeys(nextSelectedKeys);
  };
  
  // Handle user transfer confirmation
  const handleUserTransferConfirm = async () => {
    if (!selectedGroup) return;
    
    const currentMembers = groupMembers;
    const newMembers = selectedUserKeys;
    
    // Find users to add (in newMembers but not in currentMembers)
    const usersToAdd = newMembers.filter(key => !currentMembers.includes(key));
    
    // Find users to remove (in currentMembers but not in newMembers)
    const usersToRemove = currentMembers.filter(key => !newMembers.includes(key));
    
    // Perform add/remove operations
    if (usersToAdd.length > 0) {
      await addUsersToGroup(selectedGroup.id, usersToAdd);
    }
    
    if (usersToRemove.length > 0) {
      await removeUsersFromGroup(selectedGroup.id, usersToRemove);
    }
    
    // Close the drawer
    setUserTransferVisible(false);
  };
  
  // Show group details drawer
  const showGroupDetails = (group) => {
    setSelectedGroup(group);
    setGroupDetailsVisible(true);
    fetchGroupDetails(group.id);
  };
  
  // Show message modal for a group
  const showMessageModal = (group) => {
    setSelectedGroup(group);
    messageForm.resetFields();
    messageForm.setFieldsValue({
      send_notification: true
    });
    setMessageModalVisible(true);
  };
  
  // Custom filter function for transfer component
  const filterOption = (inputValue, option) => {
    return option.title.toLowerCase().indexOf(inputValue.toLowerCase()) > -1 ||
           option.description.toLowerCase().indexOf(inputValue.toLowerCase()) > -1;
  };
  
  // Table columns for user groups
  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Button 
          type="link" 
          onClick={() => showGroupDetails(record)}
          className="p-0 text-blue-500"
        >
          {text}
        </Button>
      )
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true
    },
    {
      title: 'Members',
      dataIndex: 'users_count',
      key: 'users_count',
      render: (count) => (
        <Tag color={count > 0 ? 'blue' : 'default'}>
          <TeamOutlined className="mr-1" />
          {count || 0} {count === 1 ? 'Member' : 'Members'}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="Send Message">
            <Button
              type="text"
              icon={<MailOutlined />}
              onClick={() => showMessageModal(record)}
              className="text-blue-500"
              disabled={!record.users_count}
            />
          </Tooltip>
          <Tooltip title="Manage Members">
            <Button
              type="text"
              icon={<UsergroupAddOutlined />}
              onClick={() => showUserTransfer(record)}
              className="text-green-500"
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => showGroupModal('edit', record)}
              className="text-yellow-500"
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Popconfirm
              title="Are you sure you want to delete this group?"
              onConfirm={() => deleteUserGroup(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      )
    }
  ];
  
  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">User Groups</h1>
        <div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => showGroupModal('create')}
            className="bg-golden-navy"
          >
            Create Group
          </Button>
        </div>
      </div>
      
      <Card className="shadow-md mb-6">
        <Tabs 
          activeKey={activeTab}
          onChange={setActiveTab}
          tabBarExtraContent={
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchUserGroups}
              loading={loading}
            >
              Refresh
            </Button>
          }
        >
          <TabPane 
            tab={
              <span>
                <TeamOutlined /> User Groups
              </span>
            }
            key="groups"
          >
            <MyTable
              columns={columns}
              dataSource={groups}
              rowKey="id"
              loading={loading}
              pagination={pagination}
              onChange={(pagination) => setPagination(pagination)}
            />
          </TabPane>
        </Tabs>
      </Card>
      
      {/* Group Create/Edit Modal */}
      <Modal
        title={groupModalMode === 'create' ? 'Create User Group' : 'Edit User Group'}
        open={groupModalVisible}
        onCancel={() => setGroupModalVisible(false)}
        onOk={handleGroupFormSubmit}
        okText={groupModalMode === 'create' ? 'Create' : 'Save'}
        okButtonProps={{ className: 'bg-golden-navy' }}
      >
        <Form
          form={groupForm}
          layout="vertical"
        >
          <Form.Item
            name="name"
            label="Group Name"
            rules={[{ required: true, message: 'Please enter a group name' }]}
          >
            <Input placeholder="Enter group name" />
          </Form.Item>
          
          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea
              rows={4}
              placeholder="Enter group description"
            />
          </Form.Item>
        </Form>
      </Modal>
      
      {/* User Transfer Drawer */}
      <Drawer
        title={`Manage Members - ${selectedGroup?.name || ''}`}
        width={600}
        open={userTransferVisible}
        onClose={() => setUserTransferVisible(false)}
        extra={
          <Button 
            type="primary" 
            onClick={handleUserTransferConfirm}
            className="bg-golden-navy"
          >
            Save Changes
          </Button>
        }
      >
        <div className="mb-4">
          <Input
            placeholder="Search users..."
            prefix={<SearchOutlined />}
            value={searchUserText}
            onChange={(e) => setSearchUserText(e.target.value)}
          />
        </div>
        
        {loadingUsers ? (
          <div className="flex justify-center items-center h-64">
            <Spin />
          </div>
        ) : (
          <Transfer
            dataSource={allUsers}
            showSearch
            filterOption={filterOption}
            searchPlaceholder="Search users"
            targetKeys={selectedUserKeys}
            onChange={handleUserTransferChange}
            onSearch={setSearchUserText}
            render={item => (
              <div className="flex items-center">
                <Avatar 
                  size="small" 
                  icon={<UserOutlined />} 
                  src={item.avatar}
                  className="mr-2"
                />
                <div>
                  <div>{item.title}</div>
                  <div className="text-xs text-gray-500">{item.description}</div>
                </div>
              </div>
            )}
            listStyle={{
              width: 250,
              height: 400,
            }}
            titles={['Available Users', 'Group Members']}
          />
        )}
      </Drawer>
      
      {/* Group Details Drawer */}
      <Drawer
        title="Group Details"
        width={520}
        open={groupDetailsVisible}
        onClose={() => setGroupDetailsVisible(false)}
        extra={
          <Space>
            <Button
              icon={<EditOutlined />}
              onClick={() => {
                setGroupDetailsVisible(false);
                showGroupModal('edit', selectedGroup);
              }}
            >
              Edit
            </Button>
            <Button
              icon={<UsergroupAddOutlined />}
              onClick={() => {
                setGroupDetailsVisible(false);
                showUserTransfer(selectedGroup);
              }}
              className="mr-2"
            >
              Manage Members
            </Button>
            <Button
              type="primary"
              icon={<MailOutlined />}
              onClick={() => {
                setGroupDetailsVisible(false);
                showMessageModal(selectedGroup);
              }}
              className="bg-golden-navy"
              disabled={!selectedGroup?.members?.length}
            >
              Message Group
            </Button>
          </Space>
        }
      >
        {detailsLoading ? (
          <div className="flex justify-center items-center h-64">
            <Spin />
          </div>
        ) : selectedGroup ? (
          <div>
            <div className="mb-6">
              <Title level={4}>{selectedGroup.name}</Title>
              <Text type="secondary">{selectedGroup.description || 'No description'}</Text>
            </div>
            
            <Divider orientation="left">
              Members ({selectedGroup.members?.length || 0})
            </Divider>
            
            {selectedGroup.members?.length > 0 ? (
              <List
                dataSource={selectedGroup.members}
                renderItem={member => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={<Avatar src={member.avatar} icon={<UserOutlined />} />}
                      title={member.name || 'User'}
                      description={member.email}
                    />
                  </List.Item>
                )}
                className="max-h-96 overflow-y-auto"
              />
            ) : (
              <Empty description="No members in this group" />
            )}
          </div>
        ) : (
          <Empty description="Group not found" />
        )}
      </Drawer>
      
      {/* Send Message Modal */}
      <Modal
        title={`Send Message to ${selectedGroup?.name || 'Group'}`}
        open={messageModalVisible}
        onCancel={() => setMessageModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setMessageModalVisible(false)}>
            Cancel
          </Button>,
          <Button
            key="send"
            type="primary"
            icon={<SendOutlined />}
            loading={sendingMessage}
            onClick={() => messageForm.submit()}
            className="bg-golden-navy"
          >
            Send Message
          </Button>
        ]}
        width={600}
      >
        <Form
          form={messageForm}
          layout="vertical"
          onFinish={sendMessageToGroup}
        >
          <Form.Item
            name="subject"
            label="Subject"
            rules={[{ required: true, message: 'Please enter a subject' }]}
          >
            <Input placeholder="Enter message subject" />
          </Form.Item>
          
          <Form.Item
            name="message"
            label="Message"
            rules={[{ required: true, message: 'Please enter a message' }]}
          >
            <TextArea
              rows={6}
              placeholder="Enter your message here..."
            />
          </Form.Item>
          
          <Form.Item
            name="importance"
            label="Importance"
            initialValue="normal"
          >
            <Select>
              <Option value="normal">Normal</Option>
              <Option value="high">High</Option>
            </Select>
          </Form.Item>
          
          <Form.Item
            name="send_notification"
            valuePropName="checked"
            initialValue={true}
          >
            <Space align="baseline">
              <Switch defaultChecked />
              <span>Send notification to recipients</span>
            </Space>
          </Form.Item>
          
          <div className="bg-gray-50 p-3 rounded border mt-4">
            <div className="flex items-center mb-2">
              <InfoCircleOutlined className="text-blue-500 mr-2" />
              <Text strong>Group Information</Text>
            </div>
            <Paragraph>
              This message will be sent to {selectedGroup?.members?.length || 0} members in the "{selectedGroup?.name}" group.
            </Paragraph>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminUserGroups;